import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { ShieldCheck, ShieldX, Loader2, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { toast } from 'sonner';

function formatDate(str) {
  if (!str) return '-';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminVerifications() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter) params.status = filter;
      const { data } = await adminAPI.getVerifications(params);
      setRequests(data.requests || []);
      setTotalPages(data.pages || 1);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [page, filter]);

  const handleApprove = async (id) => {
    try {
      await adminAPI.approveVerification(id);
      toast.success('Verification approved');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleReject = async () => {
    try {
      await adminAPI.rejectVerification(rejectId, { admin_notes: rejectNotes });
      toast.success('Verification rejected');
      setRejectId(null);
      setRejectNotes('');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-verifications">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Seller Verifications</h1>
        <p className="text-sm text-zinc-500 mt-1">Review identity verification requests</p>
      </div>

      <div className="flex gap-2 mb-6" data-testid="ver-filters">
        {['', 'pending', 'approved', 'rejected'].map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-[#002FA7] text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            data-testid={`ver-filter-${f || 'all'}`}
          >{f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}</button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid="verifications-table">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 text-[#002FA7] animate-spin mx-auto" /></div>
        ) : requests.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-400">No verification requests found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">ID Type</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Submitted</th>
                  <th className="text-left px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.request_id} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`ver-row-${r.request_id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-zinc-900">{r.user?.name || r.user?.email || '-'}</p>
                      <p className="text-xs text-zinc-400">{r.user?.email}</p>
                    </td>
                    <td className="px-6 py-4 text-zinc-600 capitalize">{r.id_type?.replace('_', ' ')}</td>
                    <td className="px-6 py-4 text-zinc-500 text-xs">{formatDate(r.created_at)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : r.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.document_url && (
                          <button onClick={() => setPreviewUrl(r.document_url)} className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-zinc-600 border border-zinc-200 hover:bg-zinc-50" data-testid={`view-doc-${r.request_id}`}>
                            <Eye className="w-3 h-3 inline mr-1" />View
                          </button>
                        )}
                        {r.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(r.request_id)} className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-emerald-600 border border-emerald-200 hover:bg-emerald-50" data-testid={`approve-${r.request_id}`}>
                              <ShieldCheck className="w-3 h-3 inline mr-1" />Approve
                            </button>
                            <button onClick={() => setRejectId(r.request_id)} className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50" data-testid={`reject-${r.request_id}`}>
                              <ShieldX className="w-3 h-3 inline mr-1" />Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-600 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-600 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewUrl(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg mx-4 p-4" onClick={e => e.stopPropagation()}>
            <img src={previewUrl} alt="ID Document" className="max-h-[60vh] rounded-lg" />
            <button onClick={() => setPreviewUrl(null)} className="mt-3 w-full py-2 text-sm font-bold text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50">Close</button>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="reject-dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="font-bold text-zinc-900 mb-3">Reject Verification</h3>
            <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={3} placeholder="Reason for rejection (optional)"
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] resize-none mb-4" data-testid="reject-notes"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setRejectId(null); setRejectNotes(''); }} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50">Cancel</button>
              <button onClick={handleReject} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700" data-testid="reject-confirm">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
